import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import styled from 'styled-components';
import Card from 'components/Card';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import Icon from 'components/Icon';
import Loading from 'components/Loading';
import PageHeaderIcon from 'components/PageHeaderIcon';
import { POSDatePill } from 'components/POS';
import { CashDrawerReportPrint } from './CashDrawerReport/CashDrawerReportPrint';
import PrintCashDrawerReport from './CashDrawerReport/PrintCashDrawerReport';
import { useGetCashDrawerReport } from './gql/mutation/useGetCashDrawerReport.mutation';
import { PrintingModal } from './PrintingModal';

const PageContainer = styled.div`
  padding: 20px;
  min-height: 100vh;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.background?.primary ||
        'linear-gradient(180deg, #0a0a1a 0%, #1a1a2e 100%)'
      : '#f8fafc'};
`;

const HeaderCard = styled(Card)`
  margin-bottom: 24px;
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const HeaderTitle = styled.h1`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#0F172A'};
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
  }
`;

const ContentCard = styled(Card)`
  padding: 0;
`;

const ContentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0'};
`;

const ReportTitle = styled.h2`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 22px;
  font-weight: 700;
  margin: 0;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#0F172A'};
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const UserName = styled.span`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#3C4144'};
`;

const ContentWrapper = styled.div`
  padding: 24px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
`;

// Table Styles
const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const TableHeader = styled.thead`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc'};
`;

const TableHeaderCell = styled.th`
  padding: 14px 16px;
  text-align: left;
  font-weight: 600;
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.muted || '#94A3B8'
      : '#64748B'};
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0'};
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#E2E8F0'};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.02)'
        : 'rgba(0, 0, 0, 0.02)'};
  }
`;

const TableCell = styled.td`
  padding: 14px 16px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#0F172A'};
`;

const SectionTitle = styled.h3`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#0F172A'};
`;

// Event Header Styles
const EventHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  padding: 0;
`;

const EventHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
`;

const EventName = styled.h2`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 32px;
  font-weight: 700;
  margin: 0;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#000033'};
`;

const EventLogo = styled.img`
  width: 100%;
  max-width: 110px;
  height: auto;
  display: block;
`;

export const CashDrawerReport = () => {
  const [cashDrawerReport, setCashDrawerReport] = useState();
  const { id } = useParams();

  const [isPrinting, setIsPrinting] = useState(false);

  const location = useLocation();
  const { event } = location.state || {};

  const getCashDrawerReport = useGetCashDrawerReport();

  const eventData = event || cashDrawerReport?.event;

  useEffect(() => {
    const getData = async () => {
      const response = await getCashDrawerReport(id);

      setCashDrawerReport(response.data?.getPOSRegisterReport);
    };

    getData();
  }, [getCashDrawerReport, id]);

  const transactionColumns = [
    { label: 'Cash', key: 'cash' },
    { label: 'Card', key: 'card' },
    { label: 'No of Transactions', key: 'num_transactions' },
  ];

  const ticketColumns = [
    { label: 'Ticket Name', key: 'name' },
    { label: 'Quantity', key: 'qty' },
    { label: 'Price', key: 'price' },
  ];

  const transactionData = cashDrawerReport
    ? [
        {
          cash: cashDrawerReport.cash,
          card: cashDrawerReport.card,
          num_transactions: cashDrawerReport.num_transactions,
        },
      ]
    : [];

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Event Header */}
        {eventData && (
          <EventHeaderContainer>
            <EventHeaderLeft>
              <POSDatePill
                startDate={eventData.start_date}
                endDate={eventData.end_date}
                isMultiDay={eventData.isMultiDay}
              />
              <EventName>{eventData.name}</EventName>
            </EventHeaderLeft>
            {(eventData.logo || eventData.track?.logo) && (
              <EventLogo
                src={eventData.logo ?? eventData.track?.logo}
                alt="logo"
              />
            )}
          </EventHeaderContainer>
        )}

        {/* Header Card with Actions */}
        <HeaderCard>
          <HeaderContainer>
            <HeaderLeft>
              <PageHeaderIcon icon="transactions" />
              <HeaderTitle>My Box Office Report</HeaderTitle>
            </HeaderLeft>

            <ButtonContainer>
              <PrintCashDrawerReport
                cashDrawerReport={cashDrawerReport}
                setIsPrinting={setIsPrinting}
              />

              <CashDrawerReportPrint
                eventId={id}
                date={event?.start_date}
                disable={!cashDrawerReport?.results?.length}
                transactionData={cashDrawerReport}
              />
            </ButtonContainer>
          </HeaderContainer>
        </HeaderCard>

        {/* Report Content */}
        <ContentCard noPadding>
          <ContentHeader>
            <ReportTitle>Transaction Summary</ReportTitle>
            {cashDrawerReport && (
              <UserInfo>
                <Icon icon="Dashboard-User-Profile" size={20} color="#64748B" />
                <UserName>{cashDrawerReport.user?.formatted_name}</UserName>
              </UserInfo>
            )}
          </ContentHeader>

          {cashDrawerReport ? (
            <ContentWrapper>
              {/* Transaction Summary Table */}
              <TableContainer>
                <SectionTitle>Payment Overview</SectionTitle>
                <StyledTable>
                  <TableHeader>
                    <tr>
                      {transactionColumns.map((col) => (
                        <TableHeaderCell key={col.key}>
                          {col.label}
                        </TableHeaderCell>
                      ))}
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {transactionData.map((row, idx) => (
                      <TableRow key={idx}>
                        {transactionColumns.map((col) => (
                          <TableCell key={col.key}>{row[col.key]}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </StyledTable>
              </TableContainer>

              {/* Ticket Breakdown Table */}
              <TableContainer>
                <SectionTitle>Ticket Breakdown</SectionTitle>
                <StyledTable>
                  <TableHeader>
                    <tr>
                      {ticketColumns.map((col) => (
                        <TableHeaderCell key={col.key}>
                          {col.label}
                        </TableHeaderCell>
                      ))}
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {cashDrawerReport.ticket_breakdown_data?.map((row, idx) => (
                      <TableRow key={idx}>
                        {ticketColumns.map((col) => (
                          <TableCell key={col.key}>{row[col.key]}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </StyledTable>
              </TableContainer>
            </ContentWrapper>
          ) : (
            <LoadingContainer>
              <Loading />
            </LoadingContainer>
          )}
        </ContentCard>

        <PrintingModal isVisible={isPrinting} />
      </PageContainer>
    </EnhancedThemeProvider>
  );
};
